import { useState, useEffect } from 'react';
import accessibilityService from '../../services/accessibilityService';
import './AccessibilityDashboard.css';

function AccessibilityDashboard() {
  const [overview, setOverview] = useState(null);
  const [altTextData, setAltTextData] = useState(null);
  const [transcriptData, setTranscriptData] = useState(null);
  const [wcagChecklist, setWcagChecklist] = useState([]);
  const [issues, setIssues] = useState([]);
  const [issuesSummary, setIssuesSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [issueFilters, setIssueFilters] = useState({ type: '', status: '', category: '' });
  const [runningAudit, setRunningAudit] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    const fetchIssues = async () => {
      try {
        const response = await accessibilityService.getIssues(issueFilters);
        setIssues(response.data);
        setIssuesSummary(response.summary);
      } catch (err) {
        console.error('Failed to load issues:', err);
      }
    };

    if (activeTab === 'issues') {
      fetchIssues();
    }
  }, [activeTab, issueFilters]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [overviewRes, altTextRes, transcriptRes, wcagRes, issuesRes] = await Promise.all([
        accessibilityService.getAuditOverview(),
        accessibilityService.getAltTextCoverage(),
        accessibilityService.getTranscriptStatus(),
        accessibilityService.getWcagChecklist(),
        accessibilityService.getIssues(),
      ]);

      setOverview(overviewRes.data);
      setAltTextData(altTextRes.data);
      setTranscriptData(transcriptRes.data);
      setWcagChecklist(wcagRes.data);
      setIssues(issuesRes.data);
      setIssuesSummary(issuesRes.summary);
    } catch (err) {
      setError('Failed to load accessibility data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadIssues = async () => {
    try {
      const response = await accessibilityService.getIssues(issueFilters);
      setIssues(response.data);
      setIssuesSummary(response.summary);
    } catch (err) {
      console.error('Failed to load issues:', err);
    }
  };

  const handleRunAudit = async () => {
    try {
      setRunningAudit(true);
      const response = await accessibilityService.runAudit();
      setOverview(response.data);
      await loadDashboardData();
    } catch (err) {
      console.error('Failed to run audit:', err);
    } finally {
      setRunningAudit(false);
    }
  };

  const handleUpdateIssueStatus = async (issueId, newStatus) => {
    try {
      await accessibilityService.updateIssue(issueId, { status: newStatus });
      await loadIssues();
    } catch (err) {
      console.error('Failed to update issue:', err);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'var(--color-success)';
    if (score >= 70) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pass': return 'badge-success';
      case 'partial': return 'badge-warning';
      case 'fail': return 'badge-error';
      default: return 'badge-default';
    }
  };

  const getIssueBadgeClass = (type) => {
    switch (type) {
      case 'critical': return 'badge-error';
      case 'serious': return 'badge-warning';
      case 'moderate': return 'badge-info';
      default: return 'badge-default';
    }
  };

  const getIssueStatusBadgeClass = (status) => {
    switch (status) {
      case 'open': return 'badge-error';
      case 'in_progress': return 'badge-warning';
      case 'resolved': return 'badge-success';
      default: return 'badge-default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="accessibility-dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading accessibility data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="accessibility-dashboard">
        <div className="error-container">
          <p>{error}</p>
          <button onClick={loadDashboardData} className="btn btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="accessibility-dashboard">
      <div className="page-header">
        <div>
          <h1>Accessibility Dashboard</h1>
          <p className="page-subtitle">Monitor and manage WCAG compliance across your content</p>
        </div>
        <button
          onClick={handleRunAudit}
          disabled={runningAudit}
          className="btn btn-primary"
        >
          {runningAudit ? (
            <>
              <span className="spinner-small"></span>
              Running Audit...
            </>
          ) : (
            'Run New Audit'
          )}
        </button>
      </div>

      {/* Score Overview */}
      <div className="score-overview">
        <div className="score-card main-score">
          <div className="score-circle" style={{ borderColor: getScoreColor(overview?.overallScore) }}>
            <span className="score-value">{overview?.overallScore}</span>
            <span className="score-label">/ 100</span>
          </div>
          <div className="score-info">
            <h3>Overall Accessibility Score</h3>
            <p>WCAG {overview?.wcagLevel} Compliance</p>
            <p className="last-audit">Last audit: {formatDate(overview?.lastAuditDate)}</p>
          </div>
        </div>

        <div className="score-stats">
          <div className="stat-card">
            <span className="stat-value stat-critical">{overview?.criticalIssues}</span>
            <span className="stat-label">Critical Issues</span>
          </div>
          <div className="stat-card">
            <span className="stat-value stat-total">{overview?.totalIssues}</span>
            <span className="stat-label">Total Issues</span>
          </div>
          <div className="stat-card">
            <span className="stat-value stat-images">{altTextData?.coveragePercentage}%</span>
            <span className="stat-label">Alt Text Coverage</span>
          </div>
          <div className="stat-card">
            <span className="stat-value stat-media">{transcriptData?.coveragePercentage}%</span>
            <span className="stat-label">Transcript Coverage</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'issues' ? 'active' : ''}`}
          onClick={() => setActiveTab('issues')}
        >
          Issues ({issuesSummary?.total || 0})
        </button>
        <button
          className={`tab ${activeTab === 'wcag' ? 'active' : ''}`}
          onClick={() => setActiveTab('wcag')}
        >
          WCAG Checklist
        </button>
        <button
          className={`tab ${activeTab === 'media' ? 'active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          Media Compliance
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {/* Category Scores */}
            <div className="section">
              <h2>Compliance by Category</h2>
              <div className="category-grid">
                {overview?.categories && Object.entries(overview.categories).map(([key, cat]) => (
                  <div key={key} className="category-card">
                    <div className="category-header">
                      <h4>{key.charAt(0).toUpperCase() + key.slice(1)}</h4>
                      <span
                        className="category-score"
                        style={{ color: getScoreColor(cat.score) }}
                      >
                        {cat.score}%
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{
                          width: `${cat.score}%`,
                          backgroundColor: getScoreColor(cat.score),
                        }}
                      ></div>
                    </div>
                    <p className="category-detail">
                      {cat.compliant} of {cat.total} items compliant
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Audits */}
            <div className="section">
              <h2>Audit History</h2>
              <div className="audit-history">
                {overview?.recentAudits?.map((audit) => (
                  <div key={audit.id} className="audit-item">
                    <div className="audit-date">{formatDate(audit.date)}</div>
                    <div className="audit-score" style={{ color: getScoreColor(audit.score) }}>
                      {audit.score}
                    </div>
                    <div className="audit-issues">
                      <span className="critical">{audit.criticalIssues} critical</span>
                      <span className="total">{audit.totalIssues} total</span>
                    </div>
                    <div className="audit-runner">{audit.runBy}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'issues' && (
          <div className="issues-tab">
            {/* Filters */}
            <div className="filters">
              <select
                value={issueFilters.type}
                onChange={(e) => setIssueFilters({ ...issueFilters, type: e.target.value })}
              >
                <option value="">All Severity</option>
                <option value="critical">Critical</option>
                <option value="serious">Serious</option>
                <option value="moderate">Moderate</option>
              </select>
              <select
                value={issueFilters.status}
                onChange={(e) => setIssueFilters({ ...issueFilters, status: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
              <select
                value={issueFilters.category}
                onChange={(e) => setIssueFilters({ ...issueFilters, category: e.target.value })}
              >
                <option value="">All Categories</option>
                <option value="Images">Images</option>
                <option value="Media">Media</option>
                <option value="Forms">Forms</option>
                <option value="Links">Links</option>
              </select>
            </div>

            {/* Issues List */}
            <div className="issues-list">
              {issues.map((issue) => (
                <div key={issue.id} className="issue-card">
                  <div className="issue-header">
                    <div className="issue-badges">
                      <span className={`badge ${getIssueBadgeClass(issue.type)}`}>
                        {issue.type}
                      </span>
                      <span className={`badge ${getIssueStatusBadgeClass(issue.status)}`}>
                        {issue.status.replace('_', ' ')}
                      </span>
                      <span className="badge badge-default">
                        WCAG {issue.wcagCriterion}
                      </span>
                    </div>
                    <div className="issue-actions">
                      {issue.status === 'open' && (
                        <button
                          onClick={() => handleUpdateIssueStatus(issue.id, 'in_progress')}
                          className="btn btn-sm btn-secondary"
                        >
                          Start Fix
                        </button>
                      )}
                      {issue.status === 'in_progress' && (
                        <button
                          onClick={() => handleUpdateIssueStatus(issue.id, 'resolved')}
                          className="btn btn-sm btn-success"
                        >
                          Mark Resolved
                        </button>
                      )}
                    </div>
                  </div>
                  <h3 className="issue-title">{issue.title}</h3>
                  <p className="issue-description">{issue.description}</p>
                  <div className="issue-details">
                    <div className="detail">
                      <strong>Location:</strong> {issue.location}
                    </div>
                    <div className="detail">
                      <strong>Element:</strong>
                      <code>{issue.element}</code>
                    </div>
                    <div className="detail recommendation">
                      <strong>Recommendation:</strong> {issue.recommendation}
                    </div>
                  </div>
                </div>
              ))}

              {issues.length === 0 && (
                <div className="empty-state">
                  <p>No issues found matching the selected filters.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'wcag' && (
          <div className="wcag-tab">
            <div className="wcag-legend">
              <span className="legend-item">
                <span className="badge badge-success">Pass</span> Fully compliant
              </span>
              <span className="legend-item">
                <span className="badge badge-warning">Partial</span> Some issues
              </span>
              <span className="legend-item">
                <span className="badge badge-error">Fail</span> Non-compliant
              </span>
            </div>

            <div className="wcag-list">
              {wcagChecklist.map((item) => (
                <div key={item.id} className="wcag-item">
                  <div className="wcag-criterion">
                    <span className="criterion-number">{item.criterion}</span>
                    <span className={`wcag-level level-${item.level.toLowerCase()}`}>
                      Level {item.level}
                    </span>
                  </div>
                  <div className="wcag-content">
                    <h4>{item.name}</h4>
                    <p>{item.description}</p>
                  </div>
                  <div className="wcag-status">
                    <span className={`badge ${getStatusBadgeClass(item.status)}`}>
                      {item.status}
                    </span>
                    {item.issues > 0 && (
                      <span className="issue-count">{item.issues} issues</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'media' && (
          <div className="media-tab">
            {/* Alt Text Coverage */}
            <div className="section">
              <h2>Image Alt Text Coverage</h2>
              <div className="coverage-overview">
                <div className="coverage-stat">
                  <div className="coverage-circle">
                    <svg viewBox="0 0 36 36">
                      <path
                        d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="#e0e0e0"
                        strokeWidth="3"
                      />
                      <path
                        d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke={getScoreColor(altTextData?.coveragePercentage)}
                        strokeWidth="3"
                        strokeDasharray={`${altTextData?.coveragePercentage}, 100`}
                      />
                    </svg>
                    <span className="coverage-value">{altTextData?.coveragePercentage}%</span>
                  </div>
                  <p>{altTextData?.withAltText} of {altTextData?.totalImages} images have alt text</p>
                </div>

                <div className="coverage-breakdown">
                  <h4>By Category</h4>
                  {altTextData?.byCategory?.map((cat) => (
                    <div key={cat.category} className="breakdown-item">
                      <span className="breakdown-label">{cat.category}</span>
                      <div className="breakdown-bar">
                        <div
                          className="breakdown-fill"
                          style={{
                            width: `${cat.percentage}%`,
                            backgroundColor: getScoreColor(cat.percentage),
                          }}
                        ></div>
                      </div>
                      <span className="breakdown-value">{cat.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {altTextData?.missingAltText?.length > 0 && (
                <div className="missing-items">
                  <h4>Images Missing Alt Text</h4>
                  <div className="missing-grid">
                    {altTextData.missingAltText.slice(0, 6).map((img) => (
                      <div key={img.id} className="missing-item">
                        <img src={img.url} alt="" className="missing-thumbnail" />
                        <div className="missing-info">
                          <p className="missing-name">{img.fileName}</p>
                          <p className="missing-location">{img.location}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Transcript Coverage */}
            <div className="section">
              <h2>Media Transcript Coverage</h2>
              <div className="coverage-overview">
                <div className="coverage-stat">
                  <div className="coverage-circle">
                    <svg viewBox="0 0 36 36">
                      <path
                        d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="#e0e0e0"
                        strokeWidth="3"
                      />
                      <path
                        d="M18 2.0845
                          a 15.9155 15.9155 0 0 1 0 31.831
                          a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke={getScoreColor(transcriptData?.coveragePercentage)}
                        strokeWidth="3"
                        strokeDasharray={`${transcriptData?.coveragePercentage}, 100`}
                      />
                    </svg>
                    <span className="coverage-value">{transcriptData?.coveragePercentage}%</span>
                  </div>
                  <p>{transcriptData?.withTranscripts} of {transcriptData?.totalMedia} media files have transcripts</p>
                </div>

                <div className="coverage-breakdown">
                  <h4>By Type</h4>
                  {transcriptData?.byType?.map((type) => (
                    <div key={type.type} className="breakdown-item">
                      <span className="breakdown-label">{type.type}</span>
                      <div className="breakdown-bar">
                        <div
                          className="breakdown-fill"
                          style={{
                            width: `${type.percentage}%`,
                            backgroundColor: getScoreColor(type.percentage),
                          }}
                        ></div>
                      </div>
                      <span className="breakdown-value">{type.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {transcriptData?.missingTranscripts?.length > 0 && (
                <div className="missing-items">
                  <h4>Media Missing Transcripts</h4>
                  <table className="missing-table">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Type</th>
                        <th>Duration</th>
                        <th>Location</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transcriptData.missingTranscripts.map((media) => (
                        <tr key={media.id}>
                          <td>{media.fileName}</td>
                          <td>
                            <span className={`badge badge-${media.type === 'audio' ? 'info' : 'default'}`}>
                              {media.type}
                            </span>
                          </td>
                          <td>{media.duration}</td>
                          <td>{media.location}</td>
                          <td>
                            <button className="btn btn-sm btn-secondary">
                              Add Transcript
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AccessibilityDashboard;
